import { z } from 'zod'

// The client beacon (POST /api/telemetry/session) and frontend error
// capture (POST /api/telemetry/error). Only the browser knows the screen,
// the viewport, whether the app is installed or whether it is online, so
// packages/ui sends them; the server adds what Cloudflare knows and writes
// it all to Workers Analytics Engine -- never to Postgres, and never with a
// member id.
//
// Values are BUCKETED before they leave the browser: a width class, not a
// pixel count, so the beacon cannot serve as a fingerprint.

export const TELEMETRY_APPS = ['web', 'admin', 'huolto'] as const
export type TelemetryApp = (typeof TELEMETRY_APPS)[number]

// MUI's breakpoints, which are what the layouts actually switch on.
export const WIDTH_CLASSES = ['xs', 'sm', 'md', 'lg', 'xl'] as const
export type WidthClass = (typeof WIDTH_CLASSES)[number]

export function widthClass(px: number): WidthClass {
  if (px < 600) return 'xs'
  if (px < 900) return 'sm'
  if (px < 1200) return 'md'
  if (px < 1536) return 'lg'
  return 'xl'
}

export const CONNECTION_TYPES = ['slow-2g', '2g', '3g', '4g'] as const

export const TelemetrySessionSchema = z.object({
  app: z.enum(TELEMETRY_APPS),
  screen: z.enum(WIDTH_CLASSES),
  viewport: z.enum(WIDTH_CLASSES),
  // Rounded to the nearest 0.5 by the client.
  dpr: z.number().min(0.5).max(5),
  prefersDark: z.boolean(),
  // Launched as an installed PWA rather than in a browser tab.
  standalone: z.boolean(),
  online: z.boolean(),
  // navigator.connection.effectiveType; Chromium only.
  connection: z.enum(CONNECTION_TYPES).nullable(),
  signedIn: z.boolean(),
})
export type TelemetrySession = z.infer<typeof TelemetrySessionSchema>

export const TelemetryErrorSchema = z.object({
  app: z.enum(TELEMETRY_APPS),
  kind: z.enum(['error', 'unhandledrejection']),
  message: z.string().max(500),
  // The script URL's path, never its query string.
  source: z.string().max(300).nullable(),
  line: z.number().int().nonnegative().nullable(),
  column: z.number().int().nonnegative().nullable(),
  stack: z.string().max(4000).nullable(),
  // location.pathname of the page the error happened on.
  route: z.string().max(200),
})
export type TelemetryError = z.infer<typeof TelemetryErrorSchema>
